import { createContext, ReactNode, useContext, useMemo } from 'react';
import { DataKey } from '../lib/model';
import { environmentService } from '../services';
import { useRoute } from './RouteProvider';
import { useFetch } from '../hooks/useFetch';
import { usePageContent } from './PageContentProvider';

export interface DataContextInterface {
	stateHouse10Data: { [x: string]: string }[] | null;
	stateSenate10Data: { [x: string]: string }[] | null;
	stateEdgeCase10Data: { [x: string]: string }[] | null;
	usHouse10Data: { [x: string]: string }[] | null;
	usSenate10Data: { [x: string]: string }[] | null;
	usHouseData: { [x: string]: string }[] | null;
	usSenateData: { [x: string]: string }[] | null;
	donorStateHouse10Money: { [x: string]: string }[] | null;
	donorStateSenate10Money: { [x: string]: string }[] | null;
	donorStateWide10Money: { [x: string]: string }[] | null;
	donorUsSenate10Money: { [x: string]: string }[] | null;
	donorUsHouse10Money: { [x: string]: string }[] | null;
	donorStateHouse10Contrib: { [x: string]: string }[] | null;
	donorStateSenate10Contrib: { [x: string]: string }[] | null;
	donorStateWide10Contrib: { [x: string]: string }[] | null;
	donorUsSenate10Contrib: { [x: string]: string }[] | null;
	donorUsHouse10Contrib: { [x: string]: string }[] | null;
	donorUsSenateMoney: { [x: string]: string }[] | null;
	donorUsHouseMoney: { [x: string]: string }[] | null;
	donorUsSenateCont: { [x: string]: string }[] | null;
	donorUsHouseCont: { [x: string]: string }[] | null;
	electionCycle: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DATA_CONTEXT_DEFAULT: DataContextInterface = {
	stateHouse10Data: [],
	stateSenate10Data: [],
	stateEdgeCase10Data: [],
	usHouse10Data: [],
	usSenate10Data: [],
	usHouseData: [],
	usSenateData: [],
	donorStateHouse10Money: [],
	donorStateSenate10Money: [],
	donorStateWide10Money: [],
	donorUsSenate10Money: [],
	donorUsHouse10Money: [],
	donorStateHouse10Contrib: [],
	donorStateSenate10Contrib: [],
	donorStateWide10Contrib: [],
	donorUsSenate10Contrib: [],
	donorUsHouse10Contrib: [],
	donorUsSenateMoney: [],
	donorUsHouseMoney: [],
	donorUsSenateCont: [],
	donorUsHouseCont: [],
	electionCycle: ''
};

const DataContext = createContext(DATA_CONTEXT_DEFAULT);

// eslint-disable-next-line react-refresh/only-export-components
export function useData() {
	return useContext<DataContextInterface>(DataContext);
}

export const DataProvider = ({
	children,
	page
}: {
	children: ReactNode;
	page?: string;
}) => {
	const { selectedState, election, donorTab } = useRoute();
	const {
		ussenateData,
		ussenateDataDonorMoney,
		ushouseData,
		ushouseDataDonorMoney,
		ussenateDataDonorCont,
		ushouseDataDonorCont
	} = usePageContent();

	const url1 = useMemo(() => {
		if (page === 'state') {
			if (election === 'state') {
				return '/sites/default/files/data/state_data_10states.csv';
			} else if (election === 'congress') {
				return '/sites/default/files/data/ussenate_10states.csv';
			} else return '';
		} else if (page === 'national') {
			return ussenateData || '/sites/default/files/data/ussenate_data.csv';
		} else if (page === 'donorState') {
			if (election === 'state') {
				return '/sites/default/files/data/state_data_10states_donor_money.csv';
			} else if (election === 'congress') {
				return '/sites/default/files/data/ussenate_10states_donor_money.csv';
			} else return '';
		} else if (page === 'donorNational') {
			//	if (donorTab === 0) {
			return ussenateDataDonorMoney || '/sites/default/files/data/ussenate_data_donor_money.csv';
			//	} else if (donorTab === 1) {
			//	return '/sites/default/files/data/ussenate_data_donor_cont.csv';
			//	} else return '';
		} else return '';
	}, [page, election, ussenateData, ussenateDataDonorMoney]);

	const { data: data1 } = useFetch<{ [x: string]: string }[] | null>(
		url1,
		document.location.origin.includes('3000')
			? environmentService.get('VITE_BASE_API_URL')
			: document.location.origin
	);

	const url2 = useMemo(() => {
		if (page === 'state') {
			if (election === 'congress') {
				return '/sites/default/files/data/ushouse_10states.csv';
			} else return '';
		} else if (page === 'national') {
			return ushouseData || '/sites/default/files/data/ushouse_data.csv';
		} else if (page === 'donorState') {
			if (election === 'state') {
				return '/sites/default/files/data/state_data_10states_donor_cont.csv';
			} else if (election === 'congress') {
				return '/sites/default/files/data/ussenate_10states_donor_cont.csv';
			} else return '';
		} else if (page === 'donorNational') {
			//if (donorTab === 0) {
			return ushouseDataDonorMoney || '/sites/default/files/data/ushouse_data_donor_money.csv';
			// } else if (donorTab === 1) {
			// 	return '/sites/default/files/data/ushouse_data_donor_cont.csv';
			//} else return '';
		} else return '';
	}, [page, election, ushouseData, ushouseDataDonorMoney]);

	const { data: data2 } = useFetch<{ [x: string]: string }[] | null>(
		url2,
		document.location.origin.includes('3000')
			? environmentService.get('VITE_BASE_API_URL')
			: document.location.origin
	);

	const url3 = useMemo(() => {
		if (page === 'donorState') {
			if (election === 'congress') {
				return '/sites/default/files/data/ushouse_10states_donor_money.csv';
			} else return '';
		} else if (page === 'donorNational') {
			return ussenateDataDonorCont || '/sites/default/files/data/ussenate_data_donor_cont.csv';
		} else return '';
	}, [page, election, ussenateDataDonorCont]);

	const { data: data3 } = useFetch<{ [x: string]: string }[] | null>(
		url3,
		document.location.origin.includes('3000')
			? environmentService.get('VITE_BASE_API_URL')
			: document.location.origin
	);

	const url4 = useMemo(() => {
		if (page === 'donorState') {
			if (election === 'congress') {
				return '/sites/default/files/data/ushouse_10states_donor_cont.csv';
			} else return '';
		} else if (page === 'donorNational') {
			return ushouseDataDonorCont || '/sites/default/files/data/ushouse_data_donor_cont.csv';
		} else return '';
	}, [page, election, ushouseDataDonorCont]);

	const { data: data4 } = useFetch<{ [x: string]: string }[] | null>(
		url4,
		document.location.origin.includes('3000')
			? environmentService.get('VITE_BASE_API_URL')
			: document.location.origin
	);

	//donor state
	const donorStateHouse10Money = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 0)
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State House'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election, donorTab]);

	const donorStateSenate10Money = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 0)
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State Senate'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election, donorTab]);

	const donorStateWide10Money = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 0)
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.DonorOfficeLevel] === 'STW'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election, donorTab]);

	const donorUsSenate10Money = useMemo(() => {
		if (page === 'donorState' && election === 'congress' && donorTab === 0)
			return data1?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election, donorTab]);

	const donorUsHouse10Money = useMemo(() => {
		if (page === 'donorState' && election === 'congress' && donorTab === 0)
			return data3?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data3, selectedState, page, election, donorTab]);

	const donorStateHouse10Contrib = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 1)
			return data2
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State House'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data2, selectedState, page, election, donorTab]);

	const donorStateSenate10Contrib = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 1)
			return data2
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State Senate'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data2, selectedState, page, election, donorTab]);

	const donorStateWide10Contrib = useMemo(() => {
		if (page === 'donorState' && election === 'state' && donorTab === 1)
			return data2
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.DonorOfficeLevel] === 'STW'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data2, selectedState, page, election, donorTab]);

	const donorUsSenate10Contrib = useMemo(() => {
		if (page === 'donorState' && election === 'congress' && donorTab === 1)
			return data2?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data2, selectedState, page, election, donorTab]);

	const donorUsHouse10Contrib = useMemo(() => {
		if (page === 'donorState' && election === 'congress' && donorTab === 1)
			return data4?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data4, selectedState, page, election, donorTab]);

	//donor national
	const donorUsSenateMoney = useMemo(() => {
		if (page === 'donorNational' && donorTab === 0) return data1;
		else return null;
	}, [data1, page, donorTab]);

	const donorUsHouseMoney = useMemo(() => {
		if (page === 'donorNational' && donorTab === 0) return data2;
		else return null;
	}, [data2, page, donorTab]);

	const donorUsSenateCont = useMemo(() => {
		if (page === 'donorNational' && donorTab === 1) return data3;
		else return null;
	}, [data3, page, donorTab]);

	const donorUsHouseCont = useMemo(() => {
		if (page === 'donorNational' && donorTab === 1) return data4;
		else return null;
	}, [data4, page, donorTab]);

	//state
	const stateHouse10Data = useMemo(() => {
		if (page === 'state' && election === 'state')
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State House'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election]);

	const stateSenate10Data = useMemo(() => {
		if (page === 'state' && election === 'state')
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter(
					(d: { [x: string]: string }) => d[DataKey.Chamber] === 'State Senate'
				) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election]);

	const stateEdgeCase10Data = useMemo(() => {
		if (page === 'state' && election === 'state')
			return data1
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.State] === selectedState?.toUpperCase()
				)
				.filter((d: { [x: string]: string }) => {
					if (d[DataKey.OfficeSought])
						//check to make sure we are on the right data sheet that has this column
						return (
							!d[DataKey.OfficeSought].includes('SENATE') &&
							!d[DataKey.OfficeSought].includes('HOUSE')
						);
				}) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election]);

	const usSenate10Data = useMemo(() => {
		if (page === 'state' && election === 'congress')
			return data1?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page, election]);

	const usHouse10Data = useMemo(() => {
		if (page === 'state' && election === 'congress') {
			return data2?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		} else return null;
	}, [data2, selectedState, page, election]);

	//national
	const usSenateData = useMemo(() => {
		if (page === 'national')
			return data1?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data1, selectedState, page]);

	const usHouseData = useMemo(() => {
		if (page === 'national')
			return data2?.filter(
				(d: { [x: string]: string }) =>
					d[DataKey.State] === selectedState?.toUpperCase()
			) as { [x: string]: string }[] | null;
		else return null;
	}, [data2, selectedState, page]);

	const electionCycle = useMemo(() => {
		const returnData = [];
		if (page === 'national') {
			if (usHouseData !== null && usHouseData) {
				returnData.push(...usHouseData);
			}
			if (usSenateData !== null && usSenateData) {
				returnData.push(...usSenateData);
			}
		} else if (page === 'state') {
			if (stateEdgeCase10Data !== null && stateEdgeCase10Data) {
				returnData.push(...stateEdgeCase10Data);
			}
			if (stateHouse10Data !== null && stateHouse10Data) {
				returnData.push(...stateHouse10Data);
			}
			if (stateSenate10Data !== null && stateSenate10Data) {
				returnData.push(...stateSenate10Data);
			}
			if (usHouse10Data !== null && usHouse10Data) {
				returnData.push(...usHouse10Data);
			}
			if (usSenate10Data !== null && usSenate10Data) {
				returnData.push(...usSenate10Data);
			}
		} else {
			if (donorStateHouse10Money !== null && donorStateHouse10Money) {
				returnData.push(...donorStateHouse10Money);
			}
			if (donorStateSenate10Money !== null && donorStateSenate10Money) {
				returnData.push(...donorStateSenate10Money);
			}
			if (donorStateWide10Money !== null && donorStateWide10Money) {
				returnData.push(...donorStateWide10Money);
			}
			if (donorUsSenate10Money !== null && donorUsSenate10Money) {
				returnData.push(...donorUsSenate10Money);
			}
			if (donorUsHouse10Money !== null && donorUsHouse10Money) {
				returnData.push(...donorUsHouse10Money);
			}
			if (donorStateHouse10Contrib !== null && donorStateHouse10Contrib) {
				returnData.push(...donorStateHouse10Contrib);
			}
			if (donorStateSenate10Contrib !== null && donorStateSenate10Contrib) {
				returnData.push(...donorStateSenate10Contrib);
			}
			if (donorStateWide10Contrib !== null && donorStateWide10Contrib) {
				returnData.push(...donorStateWide10Contrib);
			}
			if (donorUsSenate10Contrib !== null && donorUsSenate10Contrib) {
				returnData.push(...donorUsSenate10Contrib);
			}
			if (donorUsHouse10Contrib !== null && donorUsHouse10Contrib) {
				returnData.push(...donorUsHouse10Contrib);
			}
			if (donorUsSenateMoney !== null && donorUsSenateMoney) {
				returnData.push(...donorUsSenateMoney);
			}
			if (donorUsHouseMoney !== null && donorUsHouseMoney) {
				returnData.push(...donorUsHouseMoney);
			}
			if (donorUsSenateCont !== null && donorUsSenateCont) {
				returnData.push(...donorUsSenateCont);
			}
			if (donorUsHouseCont !== null && donorUsHouseCont) {
				returnData.push(...donorUsHouseCont);
			}
		}
		const progressBar =
			[...new Set(returnData.map((row) => row.progress_bar))][0] ??
			'pre-filing';

		return progressBar.toLowerCase();
	}, [
		page,
		stateHouse10Data,
		stateSenate10Data,
		stateEdgeCase10Data,
		usHouse10Data,
		usSenate10Data,
		usHouseData,
		usSenateData,
		donorStateHouse10Money,
		donorStateSenate10Money,
		donorStateWide10Money,
		donorUsSenate10Money,
		donorUsHouse10Money,
		donorStateHouse10Contrib,
		donorStateSenate10Contrib,
		donorStateWide10Contrib,
		donorUsSenate10Contrib,
		donorUsHouse10Contrib,
		donorUsSenateMoney,
		donorUsHouseMoney,
		donorUsSenateCont,
		donorUsHouseCont
	]);

	//console.log({ electionCycle });

	// console.log('page', page);
	// console.log({
	// 	stateHouse10Data,
	// 	stateSenate10Data,
	// 	stateEdgeCase10Data,
	// 	usHouse10Data,
	// 	usSenate10Data,
	// 	usHouseData,
	// 	usSenateData,
	// 	donorStateHouse10Money,
	// 	donorStateSenate10Money,
	// 	donorStateWide10Money,
	// 	donorUsSenate10Money,
	// 	donorUsHouse10Money,
	// 	donorStateHouse10Contrib,
	// 	donorStateSenate10Contrib,
	// 	donorStateWide10Contrib,
	// 	donorUsSenate10Contrib,
	// 	donorUsHouse10Contrib,
	// 	donorUsSenateMoney,
	// 	donorUsHouseMoney,
	// 	donorUsSenateCont,
	// 	donorUsHouseCont
	// });
	return (
		<DataContext.Provider
			value={{
				stateHouse10Data,
				stateSenate10Data,
				stateEdgeCase10Data,
				usHouse10Data,
				usSenate10Data,
				usHouseData,
				usSenateData,
				donorStateHouse10Money,
				donorStateSenate10Money,
				donorStateWide10Money,
				donorUsSenate10Money,
				donorUsHouse10Money,
				donorStateHouse10Contrib,
				donorStateSenate10Contrib,
				donorStateWide10Contrib,
				donorUsSenate10Contrib,
				donorUsHouse10Contrib,
				donorUsSenateMoney,
				donorUsHouseMoney,
				donorUsSenateCont,
				donorUsHouseCont,
				electionCycle
			}}
		>
			{children}
		</DataContext.Provider>
	);
};
